import { X } from 'lucide-react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/button';
import { ClassNames } from '@/shared/classNames';

export type DialogProperties = {
  children: ReactNode;
  description?: ReactNode;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  placement?: 'center' | 'left' | 'right';
  size?: 'lg' | 'sm';
  title: string;
};

export const Dialog = ({ children, description, onOpenChange, open, placement = 'center', size = 'sm', title }: DialogProperties) => {
  const descriptionId = useId();
  const dialogReference = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) {
      return;
    }

    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogReference.current;
    const firstFocusable = dialog?.querySelector<HTMLElement>(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    firstFocusable?.focus();

    return () => previouslyFocused?.focus();
  }, [open]);

  if (!open) {
    return null;
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === 'Escape') {
      if (!event.currentTarget.contains(event.target as Node)) {
        return;
      }
      event.preventDefault();
      onOpenChange(false);
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusable = [
      ...event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ];
    const first = focusable.at(0);
    const last = focusable.at(-1);

    if (event.shiftKey && first?.matches(':focus')) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && last?.matches(':focus')) {
      event.preventDefault();
      first?.focus();
    }
  };

  return createPortal(
    <div
      className={ClassNames(
        'bg-ink/45 fixed inset-0 z-50 grid backdrop-blur-[2px]',
        placement === 'center' ? 'place-items-center p-4' : 'items-stretch p-0',
        placement === 'left' && 'justify-items-start',
        placement === 'right' && 'justify-items-end',
      )}
      role="presentation"
    >
      <button aria-label="Close dialog" className="absolute inset-0 cursor-default" onClick={() => onOpenChange(false)} type="button" />
      <dialog
        aria-describedby={description ? descriptionId : undefined}
        aria-labelledby={titleId}
        aria-modal="true"
        className={ClassNames(
          'border-line bg-raised shadow-dialog relative w-full border',
          placement === 'center' && 'max-w-lg rounded-xl',
          placement !== 'center' && 'flex h-dvh flex-col overflow-hidden rounded-none border-y-0',
          placement !== 'center' && size === 'sm' && 'max-w-sm',
          placement !== 'center' && size === 'lg' && 'max-w-2xl',
          placement === 'left' && 'border-l-0',
          placement === 'right' && 'border-r-0',
        )}
        onKeyDown={handleKeyDown}
        open
        ref={dialogReference}
      >
        <div className="border-line flex min-w-0 shrink-0 items-start justify-between gap-3 border-b px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-ink text-base font-semibold tracking-[-0.01em] [overflow-wrap:anywhere]" id={titleId}>
              {title}
            </h2>
            {description ? (
              <div className="text-muted mt-1 text-sm" id={descriptionId}>
                {description}
              </div>
            ) : null}
          </div>
          <Button aria-label="Close dialog" onClick={() => onOpenChange(false)} variant="ghost">
            <X aria-hidden="true" className="size-4" />
          </Button>
        </div>
        <div className={ClassNames('min-w-0 p-5', placement !== 'center' && 'min-h-0 flex-1 overflow-x-hidden overflow-y-auto')}>
          {children}
        </div>
      </dialog>
    </div>,
    document.body,
  );
};
